
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Student } from "./types";

export const useStudentSearch = (open: boolean, setOpen: (isOpen: boolean) => void) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch students when search query changes (debounced)
  useEffect(() => {
    // Only search if query has at least 3 characters
    if (!searchQuery || searchQuery.length < 3) {
      setStudents([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsLoading(true);
      try {
        // Query from public.fishsix_students table (moved from student schema)
        const { data, error } = await supabase
          .from('fishsix_students')
          .select('code, first_name, last_name, nickname')
          .or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,nickname.ilike.%${searchQuery}%,code.ilike.%${searchQuery}%`)
          .is('deleted_at', null)
          .limit(10);

        if (error) {
          console.error("Error fetching students:", error);
          toast({
            title: "เกิดข้อผิดพลาด",
            description: "ไม่สามารถค้นหาข้อมูลนักเรียนได้ กรุณาลองใหม่อีกครั้ง",
            variant: "destructive",
          });
          return;
        }

        // Transform data to match Student type
        const formattedStudents: Student[] = data?.map(student => ({
          id: student.code,
          full_name: `${student.first_name} ${student.last_name || ''}`.trim(),
          nickname: student.nickname || '',
          student_id: student.code,
          grade_level: '' // Not available in this query, can be added if needed
        })) || [];

        setStudents(formattedStudents);
      } catch (error) {
        console.error("Error in database query:", error);
        toast({
          title: "เกิดข้อผิดพลาด",
          description: "เกิดข้อผิดพลาดในการเชื่อมต่อ กรุณาตรวจสอบการเชื่อมต่อเครือข่าย",
          variant: "destructive",
        });
        // Always maintain array structure even on error
        setStudents([]);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    students,
    isLoading
  };
};
